import { DefineDevice, VirtualMG63, VirtualMG75V2C12, VirtualRA68_1 } from '@hid/define/define'
import { MagnetKeyType } from '@libs/types/enum'
import { reverseMap } from '@libs/utils/tools'

export enum MG63KeyMode {
  NORMAL = 0,
  RT = 1,
  DKS = 2,
  MT = 3,
  TGL = 4,
  SOCD = 5,
  RS = 6,
  END = 7,
  MPT = 8,
}

export const huochairenMagnetModeConvert = (mode: MG63KeyMode) => {
  if (mode == MG63KeyMode.DKS) {
    return MagnetKeyType.DKS
  }
  if (mode == MG63KeyMode.MT) {
    return MagnetKeyType.MT
  }
  if (mode == MG63KeyMode.END) {
    return MagnetKeyType.END
  }
  if (mode == MG63KeyMode.TGL) {
    return MagnetKeyType.TGL
  }
  if (mode == MG63KeyMode.SOCD) {
    return MagnetKeyType.KME
  }
  if (mode == MG63KeyMode.RS) {
    return MagnetKeyType.RS
  }
  if (mode == MG63KeyMode.MPT) {
    return MagnetKeyType.MPT
  }
  return MG63KeyMode.NORMAL
}

export enum MG63TGLMode {
  UNCONFIGURED = 0,
  TOGGLE_ON_PRESS = 1, // 连续触发模式（按一下切换状态）
  HOLD_TO_ACTIVATE = 2, // 长按保持模式
}

export enum MG63SOCDPolicy {
  NEUTRAL = 0, // 中和模式
  LAST_WINS = 1, // 后按下优先
  ABSOLUTE_WINS = 2, // 绝对优先
}

export interface MG63MagKeyInfo {
  releaseCalibration: number // U16
  pressCalibration: number // U16
  pressTriggerPointMm: number // U16, 单位: 0.001mm
  releaseTriggerPointMm: number // U16
  pressTriggerValueAdc: number // U16
  releaseTriggerValueAdc: number // U16
  isRTEnabled: number // U8 (0/1)
  isContinueRTEnabled: number // U8 (0/1)
  rtPressTriggerPointMm: number // U16
  rtReleaseTriggerPointMm: number // U16
  rtPressTriggerDistance: number // U16
  rtReleaseTriggerDistance: number // U16

  keyMode: MG63KeyMode // uint8_t
  topDeadZone: number // 驱动内部统一按 0.001mm 使用
  bottomDeadZone: number // 驱动内部统一按 0.001mm 使用

  // DKS 配置
  dksLowTriggerPointMm: number // U16
  dksHighTriggerPointMm: number // U16
  dksLowTrigger: number // U16
  dksHighTrigger: number // U16
  dksKeyValues: number[] // U8[4] → 长度为4的数组
  dksEventConfig: number[] // U8[DSK_EVENT_COUNT]

  // MT 配置
  mtTapKey: number // U8
  mtHoldKey: number // U8
  mtHoldTime: number // U16 (ms)

  // TGL 配置
  tglKey: number // U8
  tglHoldTime: number // U16
  tglMode: MG63TGLMode // TGLMode

  // SOCD 配置
  isSOCDEnabled: boolean // bool → 转为 boolean
  socdPartnerRow: number // uint8_t
  socdPartnerCol: number // uint8_t
  socdPolicy: MG63SOCDPolicy // SOCD_Policy
  socdPriority: number // uint8_t (0=当前键优先, 1=配对键优先)

  // RS 配置
  rsPartnerRow: number // uint8_t
  rsPartnerCol: number // uint8_t

  // END 配置
  endPressKey: number // uint8_t
  endReleaseKey: number // uint8_t
  endDelayTime: number // U16

  // MPT 配置
  mptLowTriggerPointMm: number // U16
  mptMidTriggerPointMm: number // U16
  mptHighTriggerPointMm: number // U16
  mptLowTrigger: number // U16
  mptMidTrigger: number // U16
  mptHighTrigger: number // U16
  mptLowKey: number // U8
  mptMidKey: number // U8
  mptHighKey: number // U8
}

export enum MG63SetCmd {
  SetMT = 0x30,
  SetTGL = 0x31,
  SetSOCD = 0x32,
  SetRS = 0x33,
  SetEnd = 0x34,
  SetMPT = 0x35,
  SetDKS = 0x36,
  SetTravel = 0x40,
  SetRTTravel = 0x41,
  SetDeadZone = 0x42,
  TravelTest = 0x43,
  Calibration = 0x44,
  ReadData = 0xe0,
}

const convertHuochairenFnIndexToSixSlotMatrix = (matrixIndexToKK: { [index: number]: number }, rowCount: number, rowSlotMap?: number[]) => {
  const indexToKK: { [index: number]: number } = {}

  Object.entries(matrixIndexToKK).forEach(([rawIndex, kk]) => {
    const matrixIndex = Number(rawIndex)
    const horizontalSlot = Math.floor(matrixIndex / rowCount)
    const physicalRow = matrixIndex % rowCount
    const verticalSlot = rowSlotMap?.[physicalRow] ?? physicalRow

    indexToKK[horizontalSlot * 6 + verticalSlot] = kk
  })

  return indexToKK
}

export const MG63_MAG_KEY_INFO_SIZE_LEGACY = 75
export const MG63_MAG_KEY_INFO_SIZE_V118 = 75

// 火柴人设备行程上限（单位 0.001mm = 4mm），超出此范围视为读取过快导致的脏数据
export const MG63_MAX_VALID_TRAVEL = 4000

export function decodeMagKeyInfo(buffer: Uint8Array, useV118Protocol: boolean = false): MG63MagKeyInfo {
  const expectedSize = useV118Protocol ? MG63_MAG_KEY_INFO_SIZE_V118 : MG63_MAG_KEY_INFO_SIZE_LEGACY
  if (buffer.length !== expectedSize) {
    throw new Error(`Invalid buffer size: expected ${expectedSize} bytes, got ${buffer.length}`)
  }

  const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength)

  let offset = 0

  // Helper to read U16 (little-endian)
  const readU16 = () => {
    const val = view.getUint16(offset, true) // true = littleEndian
    offset += 2
    return val
  }

  // Helper to read U8
  const readU8 = () => {
    const val = view.getUint8(offset)
    offset += 1
    return val
  }

  // Helper to read boolean (U8 → bool)
  const readBool = () => readU8() !== 0

  // Helper to read fixed-length U8 array
  const readU8Array = (len: number) => {
    const arr = []
    for (let i = 0; i < len; i++) {
      arr.push(readU8())
    }
    return arr
  }

  // === 开始解析 ===
  const releaseCalibration = readU16()
  const pressCalibration = readU16()
  const pressTriggerPointMm = readU16()
  const releaseTriggerPointMm = readU16()
  const pressTriggerValueAdc = readU16()
  const releaseTriggerValueAdc = readU16()

  const isRTEnabled = readU8()
  const isContinueRTEnabled = readU8()
  const rtPressTriggerPointMm = readU16()
  const rtReleaseTriggerPointMm = readU16()
  const rtPressTriggerDistance = readU16()
  const rtReleaseTriggerDistance = useV118Protocol ? 0 : readU16()

  const keyMode = readU8() as MG63KeyMode
  const topDeadZone = useV118Protocol ? readU16() : readU8()
  const bottomDeadZone = useV118Protocol ? readU16() : readU8()

  // DKS
  const dskLowTriggerPointMm = readU16()
  const dskHighTriggerPointMm = readU16()
  const dskLowTrigger = readU16()
  const dskHighTrigger = readU16()
  const dskKeyValues = readU8Array(4)
  const dskEventConfig = readU8Array(4) // 假设 DSK_EVENT_COUNT = 4

  // MT
  const mtTapKey = readU8()
  const mtHoldKey = readU8()
  const mtHoldTime = readU16()

  // TGL
  const tglKey = readU8()
  const tglHoldTime = readU16()
  const tglMode = readU8() as MG63TGLMode

  // SOCD
  const isSOCDEnabled = readBool()
  const socdPartnerRow = readU8()
  const socdPartnerCol = readU8()
  const socdPolicy = readU8() as MG63SOCDPolicy
  const socdPriority = readU8()

  // RS
  const rsPartnerRow = readU8()
  const rsPartnerCol = readU8()

  // END
  const endPressKey = readU8()
  const endReleaseKey = readU8()
  const endDelayTime = readU16()

  // MPT
  const mptLowTriggerPointMm = readU16()
  const mptMidTriggerPointMm = readU16()
  const mptHighTriggerPointMm = readU16()
  const mptLowTrigger = readU16()
  const mptMidTrigger = readU16()
  const mptHighTrigger = readU16()
  const mptLowKey = readU8()
  const mptMidKey = readU8()
  const mptHighKey = readU8()

  // 验证是否读完整个结构体
  if (offset !== expectedSize) {
    console.warn(`Decode offset mismatch: expected ${expectedSize}, got ${offset}`)
  }

  return {
    releaseCalibration,
    pressCalibration,
    pressTriggerPointMm,
    releaseTriggerPointMm,
    pressTriggerValueAdc,
    releaseTriggerValueAdc,
    isRTEnabled,
    isContinueRTEnabled,
    rtPressTriggerPointMm,
    rtReleaseTriggerPointMm,
    rtPressTriggerDistance,
    rtReleaseTriggerDistance,
    keyMode,
    topDeadZone,
    bottomDeadZone,
    dksLowTriggerPointMm: dskLowTriggerPointMm,
    dksHighTriggerPointMm: dskHighTriggerPointMm,
    dksLowTrigger: dskLowTrigger,
    dksHighTrigger: dskHighTrigger,
    dksKeyValues: dskKeyValues,
    dksEventConfig: dskEventConfig,
    mtTapKey,
    mtHoldKey,
    mtHoldTime,
    tglKey,
    tglHoldTime,
    tglMode,
    isSOCDEnabled,
    socdPartnerRow,
    socdPartnerCol,
    socdPolicy,
    socdPriority,
    rsPartnerRow,
    rsPartnerCol,
    endPressKey,
    endReleaseKey,
    endDelayTime,
    mptLowTriggerPointMm,
    mptMidTriggerPointMm,
    mptHighTriggerPointMm,
    mptLowTrigger,
    mptMidTrigger,
    mptHighTrigger,
    mptLowKey,
    mptMidKey,
    mptHighKey,
  }
}

const mg63MatrixIndexToKK: { [index: number]: number } = {
  0x00: 0x29,
  0x05: 0x1e,
  0x0a: 0x1f,
  0x0f: 0x20,
  0x14: 0x21,
  0x19: 0x22,
  0x1e: 0x23,
  0x23: 0x24,
  0x28: 0x25,
  0x2d: 0x26,
  0x32: 0x27,
  0x37: 0x2d,
  0x3c: 0x2e,
  0x41: 0x2a, //第一行
  // 0x46: 0x49,

  0x01: 0x2b,
  0x06: 0x14,
  0x0b: 0x1a,
  0x10: 0x8,
  0x15: 0x15,
  0x1a: 0x17,
  0x1f: 0x1c,
  0x24: 0x18,
  0x29: 0x0c,
  0x2e: 0x12,
  0x33: 0x13,
  0x38: 0x2f,
  0x3d: 0x30,
  0x42: 0x31,

  0x02: 0x39,
  0x07: 0x04,
  0x0c: 0x16,
  0x11: 0x07,
  0x16: 0x09,
  0x1b: 0x0a,
  0x20: 0x0b,
  0x25: 0x0d,
  0x2a: 0x0e,
  0x2f: 0x0f,
  0x34: 0x33,
  0x39: 0x34,
  0x3e: 0x28, // 第三行
  // 0x43: 0x4b,

  0x03: 0xe1,
  0x08: 0x1d,
  0x0d: 0x1b,
  0x12: 0x06,
  0x17: 0x19,
  0x1c: 0x05,
  0x21: 0x11,
  0x26: 0x10,
  0x2b: 0x36,
  0x30: 0x37,
  0x35: 0x38,
  0x3a: 0xe5,
  0x3f: 0x52,
  0x44: 0x4c, //第四行

  0x04: 0xe0,
  0x09: 0xe3,
  0x0e: 0xe2,
  0x13: 0x2c,
  0x18: 0xe6,
  0x1d: 0xff,
  0x22: 0x50,
  0x27: 0x51,
  0x2c: 0x4f,
}

// 写一个方法返回反转MG63MagMap的键值对
const mg63KKToMatrixIndex = reverseMap(mg63MatrixIndexToKK)

export const mg63FnMatrixIndexToKK = convertHuochairenFnIndexToSixSlotMatrix(mg63MatrixIndexToKK, 5)

const ra68MatrixIndexToKK: { [index: number]: number } = {
  0x00: 0x29,
  0x05: 0x1e,
  0x0a: 0x1f,
  0x0f: 0x20,
  0x14: 0x21,
  0x19: 0x22,
  0x1e: 0x23,
  0x23: 0x24,
  0x28: 0x25,
  0x2d: 0x26,
  0x32: 0x27,
  0x37: 0x2d,
  0x3c: 0x2e,
  0x41: 0x2a,
  0x46: 0x49, //第一行

  0x01: 0x2b,
  0x06: 0x14,
  0x0b: 0x1a,
  0x10: 0x8,
  0x15: 0x15,
  0x1a: 0x17,
  0x1f: 0x1c,
  0x24: 0x18,
  0x29: 0x0c,
  0x2e: 0x12,
  0x33: 0x13,
  0x38: 0x2f,
  0x3d: 0x30,
  0x42: 0x31,
  0x47: 0x4c,

  0x02: 0x39,
  0x07: 0x04,
  0x0c: 0x16,
  0x11: 0x07,
  0x16: 0x09,
  0x1b: 0x0a,
  0x20: 0x0b,
  0x25: 0x0d,
  0x2a: 0x0e,
  0x2f: 0x0f,
  0x34: 0x33,
  0x39: 0x34,
  0x3e: 0x28,
  0x43: 0x4b, // 第三行

  0x03: 0xe1,
  0x08: 0x1d,
  0x0d: 0x1b,
  0x12: 0x06,
  0x17: 0x19,
  0x1c: 0x05,
  0x21: 0x11,
  0x26: 0x10,
  0x2b: 0x36,
  0x30: 0x37,
  0x35: 0x38,
  0x3a: 0xe5,
  0x3f: 0x52,
  0x44: 0x4e, //第四行

  0x04: 0xe0,
  0x09: 0xe3,
  0x0e: 0xe2,
  0x13: 0x2c,
  0x18: 0xe6,
  0x1d: 0xff,
  0x22: 0xe4,
  0x27: 0x50,
  0x2c: 0x51,
  0x31: 0x4f,
}

export const ra68FnMatrixIndexToKK = convertHuochairenFnIndexToSixSlotMatrix(ra68MatrixIndexToKK, 5)

export const mg75v2MatrixIndexToKK: { [index: number]: number } = {
  /* Row 0: Index = Col * 6 + 0 */
  0x00: 0x29 /* Esc */,
  0x06: 0x3a /* F1 */,
  0x0c: 0x3b /* F2 */,
  0x12: 0x3c /* F3 */,
  0x18: 0x3d /* F4 */,
  0x1e: 0x3e /* F5 */,
  0x24: 0x3f /* F6 */,
  0x2a: 0x40 /* F7 */,
  0x30: 0x41 /* F8 */,
  0x36: 0x42 /* F9 */,
  0x3c: 0x43 /* F10 */,
  0x42: 0x44 /* F11 */,
  0x48: 0x45 /* F12 */,
  0x4e: 0x46 /* PrtSc/Print */,
  0x54: 0x4c /* Del */,

  /* Row 1: Index = Col * 6 + 1 */
  0x01: 0x35 /* ~ */,
  0x07: 0x1e /* 1 */,
  0x0d: 0x1f /* 2 */,
  0x13: 0x20 /* 3 */,
  0x19: 0x21 /* 4 */,
  0x1f: 0x22 /* 5 */,
  0x25: 0x23 /* 6 */,
  0x2b: 0x24 /* 7 */,
  0x31: 0x25 /* 8 */,
  0x37: 0x26 /* 9 */,
  0x3d: 0x27 /* 0 */,
  0x43: 0x2d /* - */,
  0x49: 0x2e /* = */,
  0x4f: 0x2a /* Backspace */,
  0x55: 0x49 /* Ins */,

  /* Row 2: Index = Col * 6 + 2 */
  0x02: 0x2b /* Tab */,
  0x08: 0x14 /* Q */,
  0x0e: 0x1a /* W */,
  0x14: 0x08 /* E */,
  0x1a: 0x15 /* R */,
  0x20: 0x17 /* T */,
  0x26: 0x1c /* Y */,
  0x2c: 0x18 /* U */,
  0x32: 0x0c /* I */,
  0x38: 0x12 /* O */,
  0x3e: 0x13 /* P */,
  0x44: 0x2f /* [ */,
  0x4a: 0x30 /* ] */,
  0x50: 0x31 /* \ */,
  0x56: 0x4b /* PgUp */,

  /* Row 3: Index = Col * 6 + 3 */
  0x03: 0x39 /* Caps */,
  0x09: 0x04 /* A */,
  0x0f: 0x16 /* S */,
  0x15: 0x07 /* D */,
  0x1b: 0x09 /* F */,
  0x21: 0x0a /* G */,
  0x27: 0x0b /* H */,
  0x2d: 0x0d /* J */,
  0x33: 0x0e /* K */,
  0x39: 0x0f /* L */,
  0x3f: 0x33 /* ; */,
  0x45: 0x34 /* ' */,
  0x4b: 0x28 /* Enter */,
  0x51: 0x4e /* PgDn */,

  /* Row 4: Index = Col * 6 + 4 */
  0x04: 0xe1 /* LShift */,
  0x0a: 0x1d /* Z */,
  0x10: 0x1b /* X */,
  0x16: 0x06 /* C */,
  0x1c: 0x19 /* V */,
  0x22: 0x05 /* B */,
  0x28: 0x11 /* N */,
  0x2e: 0x10 /* M */,
  0x34: 0x36 /* , */,
  0x3a: 0x37 /* . */,
  0x40: 0x38 /* / */,
  0x46: 0xe5 /* RShift */,
  0x4c: 0x52 /* Up */,

  /* Row 5: Index = Col * 6 + 5 */
  0x05: 0xe0 /* LCtrl */,
  0x0b: 0xe3 /* LWin */,
  0x11: 0xe2 /* LAlt */,
  0x17: 0x2c /* Space */,
  0x1d: 0xe6 /* RAlt */,
  0x23: 0xff /* Fn */,
  0x29: 0x50 /* Left */,
  0x2f: 0x51 /* Down */,
  0x35: 0x4f /* Right */,
}

const ra68KKToMatrixIndex = reverseMap(ra68MatrixIndexToKK)
const mg75v2KKToMatrixIndex = reverseMap(mg75v2MatrixIndexToKK)

const huochairenMatrixIndexToKKByDevice = new Map<DefineDevice, { [index: number]: number }>([
  [VirtualMG63, mg63MatrixIndexToKK],
  [VirtualRA68_1, ra68MatrixIndexToKK],
  [VirtualMG75V2C12, mg75v2MatrixIndexToKK],
])

const huochairenKKToMatrixIndexByDevice = new Map<DefineDevice, { [index: number]: number }>([
  [VirtualMG63, mg63KKToMatrixIndex],
  [VirtualRA68_1, ra68KKToMatrixIndex],
  [VirtualMG75V2C12, mg75v2KKToMatrixIndex],
])

const getDefaultHuochairenMatrixIndexToKK = () => {
  return huochairenMatrixIndexToKKByDevice.get(VirtualMG63) as { [index: number]: number }
}

const getDefaultHuochairenKKToMatrixIndex = () => {
  return huochairenKKToMatrixIndexByDevice.get(VirtualMG63) as { [index: number]: number }
}

export const getHuochairenMatrixIndexToKKMap = (deviceID: DefineDevice) => {
  return huochairenMatrixIndexToKKByDevice.get(deviceID) ?? getDefaultHuochairenMatrixIndexToKK()
}

export const getHuochairenKKToMatrixIndexMap = (deviceID: DefineDevice) => {
  return huochairenKKToMatrixIndexByDevice.get(deviceID) ?? getDefaultHuochairenKKToMatrixIndex()
}

export const huochairenMagMapReverse = (deviceID: DefineDevice) => {
  return getHuochairenKKToMatrixIndexMap(deviceID)
}

/**
 * 火柴人固件 Bug 适配：SOCD 配对完整性校验
 *
 * 固件在重置 SOCD 配对时，可能只清除主键的 SOCD 标志，而从键的 mode 未被正确重置。
 * 此函数在从硬件读回磁轴数据后，对所有声称为 SOCD 模式的按键进行双向配对验证，
 * 不合法的 SOCD 按键将被降级为 NORMAL 模式。
 *
 * 注意：固件的 socdPartnerRow/socdPartnerCol 与驱动矩阵约定相反，
 * 矩阵换算公式为 socdPartnerCol + socdPartnerRow * rowCount。
 */
export function validateSOCDPairs(keysData: MG63MagKeyInfo[], rowCount: number): void {
  const socdIndices: number[] = []
  for (let i = 0; i < keysData.length; i++) {
    if (keysData[i].keyMode === MG63KeyMode.SOCD) {
      socdIndices.push(i)
    }
  }

  for (const idx of socdIndices) {
    const info = keysData[idx]
    // 固件 row/col 与驱动矩阵约定相反
    const partnerMatrixIndex = info.socdPartnerCol + info.socdPartnerRow * rowCount
    const partner = keysData[partnerMatrixIndex]

    const isValid = partner !== undefined && partner.keyMode === MG63KeyMode.SOCD && partner.socdPartnerCol + partner.socdPartnerRow * rowCount === idx

    if (!isValid) {
      info.keyMode = MG63KeyMode.NORMAL
      info.socdPartnerRow = 0
      info.socdPartnerCol = 0
      info.socdPolicy = MG63SOCDPolicy.NEUTRAL
      info.socdPriority = 0
      info.isSOCDEnabled = false
    }
  }
}

export const mg63ReportMap = new Map<number, number>([
  [1, 8000],
  [2, 4000],
  [3, 2000],
  [4, 1000],
  [5, 500],
  [6, 250],
  [7, 125],
])
// Profile 配置文件字节偏移（CMD=0xE1，单配置 512 字节）
// 响应格式：CMD + 配置编号 + 偏移量 + 保留 + 有效数据(60字节)
// 超过 59 字节的偏移需分段读取（data[3] = 起始字节偏移，每次最多读 60 字节）
// #define ConfigNum_OFFSET 1
// #define ReportRate_OFFSET 16
// #define System_OFFSET 17
// #define CustomLedEnable_OFFSET 18
// #define MainLedMode_OFFSET 19
// #define CustomLedMode_OFFSET 20
// #define LogoLedMode_OFFSET 21
// #define SideLedMode_OFFSET 22
// #define Winlock_OFFSET 23
// #define KBlock_OFFSET 24
// #define FirstSleepTime_OFFSET 33
// #define SecondSleepTime_OFFSET 34
// #define FactoryDefaultBottomDeadZoneLock_OFFSET 40
// #define FactoryDefaultTopDeadZoneLock_OFFSET 41
// #define MainLedParameter_OFFSET (16+60)        // =76,  键盘背光灯效参数，从模式1开始，共19种×9字节=171字节
// #define CustomLedModeLium_OFFSET (16+60+171)   // =247, 自定义灯光亮度，1字节
// #define LogoLedParameter_OFFSET (16+60+171+9)  // =256, Logo灯效参数，从模式0(关灯)开始，每种9字节
export enum MG63KeyboardParams {
  ConfigNum_OFFSET = 1,
  ReportRate_OFFSET = 16,
  System_OFFSET = 17,
  CustomLedEnable_OFFSET = 18,
  MainLedMode_OFFSET = 19,
  CustomLedMode_OFFSET = 20,
  LogoLedMode_OFFSET = 21,
  SideLedMode_OFFSET = 22,
  Winlock_OFFSET = 23,
  KBlock_OFFSET = 24,
  DirectionKeySwap = 25,
  KeyMode = 26,
  FirstSleepTime_OFFSET = 33,
  SecondSleepTime_OFFSET = 34,
  CancelDefaultDeadZone = 40,
  FactoryDefaultBottomDeadZoneLock_OFFSET = 40,
  FactoryDefaultTopDeadZoneLock_OFFSET = 41,
  // 以下偏移超过 59，需分段读取（读取时 data[3] = 偏移量）
  MainLedParameter_OFFSET = 76, // 背光灯效参数起始：共 19 种模式 × 9 字节 = 171 字节
  CustomLedModeLium_OFFSET = 247, // 自定义灯光亮度：1 字节
  LogoLedParameter_OFFSET = 256, // Logo 灯效参数起始：从模式 0(关灯)开始，每种 9 字节
}

/** 单种灯效参数（9 字节），对应固件 LED_PARAMETER_RAM */
export interface LedParameterRam {
  /** 颜色：0-7 指定颜色(红橙黄绿青蓝紫白)，8 多彩，9 驱动指定颜色(见 LedR/G/B) */
  Config1: number
  /** 亮度：1-5（1 最暗，5 最亮） */
  Config2: number
  /** 速度：1-5（1 最慢，5 最快） */
  Config3: number
  LedR: number // 驱动设定红色值（Config1=9 时有效）
  LedG: number // 驱动设定绿色值（Config1=9 时有效）
  LedB: number // 驱动设定蓝色值（Config1=9 时有效）
  DutyR: number // 保留
  DutyG: number // 保留
  DutyB: number // 保留
}

/** 单种灯效参数字节长度 */
export const LED_PARAMETER_RAM_SIZE = 9

/** 背光灯效参数模式数（从模式1/流光开始，不含关灯） */
export const MAIN_LED_MODE_COUNT = 19

/**
 * 从字节数组中解析单个 LedParameterRam（9 字节）
 * @param bytes 原始字节数组
 * @param offset 起始偏移（默认 0）
 */
export function decodeLedParameterRam(bytes: number[], offset: number = 0): LedParameterRam {
  return {
    Config1: bytes[offset + 0] ?? 0,
    Config2: bytes[offset + 1] ?? 0,
    Config3: bytes[offset + 2] ?? 0,
    LedR: bytes[offset + 3] ?? 0,
    LedG: bytes[offset + 4] ?? 0,
    LedB: bytes[offset + 5] ?? 0,
    DutyR: bytes[offset + 6] ?? 0,
    DutyG: bytes[offset + 7] ?? 0,
    DutyB: bytes[offset + 8] ?? 0,
  }
}

/**
 * 将 LedParameterRam 编码为 9 字节数组
 */
export function encodeLedParameterRam(param: LedParameterRam): number[] {
  return [param.Config1, param.Config2, param.Config3, param.LedR, param.LedG, param.LedB, param.DutyR, param.DutyG, param.DutyB]
}

/** Logo 灯效模式数（从模式0=关灯开始），对应 KG014 背部装饰 Logo 灯 */
export const LOGO_LED_MODE_COUNT = 10

/**
 * 从 profile 读回的完整灯效参数（仅 v1.20+ 支持）
 */
export interface LightProfileParams {
  /** 背光灯效参数：共 MAIN_LED_MODE_COUNT 条，index 0 对应模式1（流光），不含关灯模式 */
  mainLedParams: LedParameterRam[]
  /** 自定义灯光亮度（1-5） */
  customLedModeLium: number
  /** Logo 灯效参数：共 LOGO_LED_MODE_COUNT 条，index 0 对应模式0（关灯） */
  logoLedParams: LedParameterRam[]
}

export const MG63CustomMap: { [index: number]: number } = {
  0x18: 0x1a,
  0x27: 0x04,
  0x2a: 0x16,
}
